import { NAV_TRIPS_PER_PAGE } from "@/constants/trips"
import { FixedDayRange } from "@/types/date"
import { InspirationRef, PlaceRef } from "@/types/entity"
import { Itinerary } from "@/types/itinerary"
import { PlacePhoto } from "@/types/place"
import {
  Collaborator,
  ItineraryReviewResponse,
  TimeFrame,
  Trip,
  TripAdviceDismissalReason,
  TripAdvicesResponse,
  TripCounts,
  TripDetail,
  TripRef,
  TripSummary,
  TripTextExtraction,
  TripUpdate,
  TripVibe,
} from "@/types/trip"
import { TripsTiming } from "@/types/trips"
import {
  GetTripsCalendarEventsArgs,
  GetTripsCalendarEventsResponse,
} from "@/types/trips-calendar"
import { UserRef } from "@/types/user"

import { dayObjectAsUrlParam, now, nowMillis } from "@/lib/date-utils"
import { PagingMeta } from "@/lib/paging"
import userSlice from "@/lib/storage/user-slice"
import { AppDispatch, RootState } from "../redux-store"
import { baseApi } from "./base-api"

export interface GetNavTripsResponse {
  trips: TripSummary[]
  meta: PagingMeta
}

export interface GetNavTripsArgs {
  search?: string
  page?: number
  perPage?: number
}

export interface GetTripsResponse {
  trips: TripSummary[]
  meta: PagingMeta
}

export interface GetTripsArgs {
  page?: number
  perPage?: number
  booked?: boolean
  synced?: boolean
  timing?: TripsTiming
  eventDayRange?: FixedDayRange
  search?: string
}

export interface GetTripDetailsResponse {
  trip_details: TripDetail[]
  meta: PagingMeta
  max_trip_details: number
}

export interface GetTripDetailsArgs {
  tripRef: TripRef
  page?: number
}

export interface TripPlace {
  place_ref: PlaceRef
  trip_ref: TripRef
}

export interface GetTripRefsForPlacesArgs {
  placeRefs: PlaceRef[]
  timing?: TripsTiming
  includeTrips?: boolean
}

export interface GetTripRefsForPlacesResponse {
  trip_places: TripPlace[]
  trips: TripSummary[]
}

// Immer recipe for the advice caches: drop the dismissed card in place, or
// replace a now-cardless ready response with an empty one (the server holds
// regeneration after a dismissal, so empty is what a refetch would return).
export function emptiedAdviceResponse(
  draft: TripAdvicesResponse
): TripAdvicesResponse | void {
  if (draft.status !== "ready") return
  return { status: "empty", trip_ref: draft.trip_ref }
}

export function withoutDismissedAdvice(
  draft: TripAdvicesResponse,
  adviceId: number
): TripAdvicesResponse | void {
  if (draft.status !== "ready") return
  const advices = draft.advices.filter((advice) => advice.id !== adviceId)
  if (advices.length === draft.advices.length) return
  if (advices.length === 0) {
    return { status: "empty", trip_ref: draft.trip_ref }
  }
  draft.advices = advices
}

export const tripsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getNavTrips: builder.query<GetNavTripsResponse, GetNavTripsArgs>({
      query: (args) => {
        const params = new URLSearchParams()
        if (args.search) params.append("search", args.search)
        if (args.page) params.append("page", args.page.toString())
        if (args.perPage) {
          params.append("per_page", args.perPage.toString())
        } else {
          params.append("per_page", NAV_TRIPS_PER_PAGE.toString())
        }

        const queryString = params.toString()
        const url = queryString ? `/api/trips?${queryString}` : "/api/trips"

        return { url }
      },
      providesTags: ["NavTrips"],
      serializeQueryArgs: ({ queryArgs }) => {
        // Use search as the cache key, or "default" if no search
        return queryArgs.search || "default"
      },
      merge: (current, incoming, { arg }) => {
        // Handle pagination merge for append behavior
        if (arg.page && arg.page > 1 && current) {
          return {
            ...incoming,
            trips: [...current.trips, ...incoming.trips],
          }
        }
        // For page 1 or no page: if we've already loaded more pages,
        // preserve the paginated data to avoid losing it on refetch/remount
        if (current && current.meta.current_page > 1) {
          return current
        }
        return incoming
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.page !== previousArg?.page
      },
    }),
    getTripsCalendarEvents: builder.query<
      GetTripsCalendarEventsResponse,
      GetTripsCalendarEventsArgs
    >({
      query: (args) => {
        const params = new URLSearchParams()
        params.append("start_date", dayObjectAsUrlParam(args.startDate))
        params.append("end_date", dayObjectAsUrlParam(args.endDate))

        return {
          url: `/api/trips/calendar-events?${params.toString()}`,
        }
      },
      providesTags: ["TripsCalendar"],
    }),
    getTrips: builder.query<GetTripsResponse, GetTripsArgs>({
      query: (args) => {
        const params = new URLSearchParams()
        if (args.page) params.append("page", args.page.toString())
        if (args.perPage) {
          params.append("per_page", args.perPage.toString())
        }
        if (args.booked) params.append("booked", args.booked.toString())
        if (args.synced) params.append("synced", args.synced.toString())
        if (args.timing) params.append("timing", args.timing)
        if (args.eventDayRange) {
          params.append(
            "event_start_date",
            dayObjectAsUrlParam(args.eventDayRange.from)
          )
          params.append(
            "event_end_date",
            dayObjectAsUrlParam(args.eventDayRange.to)
          )
        }
        if (args.search) params.append("search", args.search)

        const queryString = params.toString()
        const url = queryString ? `/api/trips?${queryString}` : "/api/trips"

        return { url }
      },
      providesTags: ["Trips"],
      serializeQueryArgs: ({ queryArgs }) => {
        // Create cache key from filter combination (excluding page for pagination merge)
        return JSON.stringify({
          booked: queryArgs.booked,
          synced: queryArgs.synced,
          timing: queryArgs.timing,
          eventDayRange: queryArgs.eventDayRange,
          search: queryArgs.search,
          perPage: queryArgs.perPage,
        })
      },
      merge: (current, incoming, { arg }) => {
        // Merge pagination results
        if (arg.page && arg.page > 1 && current) {
          return {
            ...incoming,
            trips: [...current.trips, ...incoming.trips],
          }
        }
        // For page 1 or no page: if we've already loaded more pages,
        // preserve the paginated data to avoid losing it on refetch/remount
        if (current && current.meta.current_page > 1) {
          return current
        }
        return incoming
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.page !== previousArg?.page
      },
    }),
    getTrip: builder.query<Trip, TripRef>({
      query: (tripRef) => ({ url: `/api/trips/${tripRef}` }),
      transformResponse: (response: { trip: Trip }) => response.trip,
      providesTags: (_result, _error, tripRef) => [
        { type: "Trip", id: tripRef },
      ],
    }),
    getTripCounts: builder.query<TripCounts, TripRef>({
      query: (tripRef) => ({ url: `/api/trips/${tripRef}/counts` }),
      transformResponse: (response: { trip_counts: TripCounts }) =>
        response.trip_counts,
      providesTags: (_result, _error, tripRef) => [
        { type: "TripCounts", id: tripRef },
      ],
    }),
    getTripAdvices: builder.query<TripAdvicesResponse, TripRef>({
      query: (tripRef) => ({
        url: `/api/trips/${tripRef}/advices`,
      }),
      providesTags: (_result, _error, tripRef) => [
        { type: "TripAdvice", id: tripRef },
      ],
    }),
    getContextualTripAdvices: builder.query<TripAdvicesResponse, void>({
      query: (_args) => ({
        url: "/api/trips/advices",
      }),
      providesTags: [{ type: "TripAdvice", id: "contextual" }],
    }),
    dismissTripAdvice: builder.mutation<
      void,
      {
        adviceId: number
        tripRef?: TripRef | null
        reason: TripAdviceDismissalReason
        note?: string
        surface?: "TripHub" | "AppHomeTripCard"
      }
    >({
      query: ({ adviceId, reason, note }) => ({
        url: `/api/trips/advices/${adviceId}/dismiss`,
        method: "POST",
        data: { reason, ...(note ? { note } : {}) },
      }),
      transformResponse: () => undefined,
      onQueryStarted: async (
        { adviceId, tripRef, surface },
        { dispatch, queryFulfilled }
      ) => {
        dispatch(
          tripsApi.util.updateQueryData(
            "getContextualTripAdvices",
            undefined,
            (draft) =>
              // Home shows one card at a time with no set-size cue, so a
              // sibling popping into a just-dismissed slot reads as an
              // endless supply. Quiet the whole slot there instead.
              surface === "AppHomeTripCard"
                ? emptiedAdviceResponse(draft)
                : withoutDismissedAdvice(draft, adviceId)
          )
        )
        if (tripRef) {
          dispatch(
            tripsApi.util.updateQueryData("getTripAdvices", tripRef, (draft) =>
              withoutDismissedAdvice(draft, adviceId)
            )
          )
        }

        try {
          await queryFulfilled
        } catch {
          // Refetch instead of patch.undo(): when dismissals overlap, a stale
          // inverse patch can resurrect a card another dismissal already
          // removed (worst on the ready→empty whole-object transition).
          dispatch(
            tripsApi.util.invalidateTags([
              { type: "TripAdvice", id: "contextual" },
              ...(tripRef
                ? [{ type: "TripAdvice" as const, id: tripRef }]
                : []),
            ])
          )
        }
      },
    }),
    recordTripAdvicePillClick: builder.mutation<
      null,
      { adviceId: number; pill: { type: string; label: string } }
    >({
      query: ({ adviceId, pill }) => ({
        url: `/api/trips/advices/${adviceId}/pill-click`,
        method: "POST",
        data: { pill },
      }),
      transformResponse: () => null,
    }),
    snoozeTripAdvices: builder.mutation<null, { tripRef: TripRef }>({
      query: ({ tripRef }) => ({
        url: `/api/trips/${tripRef}/advices/snooze`,
        method: "POST",
      }),
      transformResponse: () => null,
      invalidatesTags: (_result, _error, { tripRef }) => [
        { type: "TripAdvice", id: tripRef },
        { type: "TripAdvice", id: "contextual" },
      ],
    }),
    unsnoozeTripAdvices: builder.mutation<null, { tripRef: TripRef }>({
      query: ({ tripRef }) => ({
        url: `/api/trips/${tripRef}/advices/snooze`,
        method: "DELETE",
      }),
      transformResponse: () => null,
      invalidatesTags: (_result, _error, { tripRef }) => [
        { type: "TripAdvice", id: tripRef },
        { type: "TripAdvice", id: "contextual" },
      ],
    }),
    recordTripAdviceView: builder.mutation<null, { adviceId: number }>({
      query: ({ adviceId }) => ({
        url: `/api/trips/advices/${adviceId}/view`,
        method: "POST",
      }),
      transformResponse: () => null,
    }),
    getItineraryReview: builder.query<ItineraryReviewResponse, TripRef>({
      query: (tripRef) => ({
        url: `/api/trips/${tripRef}/itinerary-review`,
      }),
      providesTags: (_result, _error, tripRef) => [
        { type: "ItineraryReview", id: tripRef },
      ],
    }),
    startItineraryReview: builder.mutation<ItineraryReviewResponse, TripRef>({
      query: (tripRef) => ({
        url: `/api/trips/${tripRef}/itinerary-review`,
        method: "POST",
      }),
      // The POST runs the review synchronously and returns the final result;
      // upsert it into the query cache so the button reflects the outcome
      // without a separate GET.
      async onQueryStarted(tripRef, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled
        void dispatch(
          tripsApi.util.upsertQueryData("getItineraryReview", tripRef, data)
        )
      },
    }),
    leaveTrip: builder.mutation<void, TripRef>({
      query: (tripRef) => ({
        url: `/api/trips/${tripRef}/leave`,
        method: "POST",
      }),
      transformResponse: () => undefined,
      async onQueryStarted(tripRef, { dispatch, queryFulfilled }) {
        // optimistically remove the trip from all possible cached getTrips queries
        // we update all known filter combinations since rtk query cache keys
        // are the serialized args, not prefixed with endpoint name
        const patches: Array<{ undo: () => void }> = []

        // all possible getTrips cache key combinations based on serializeQueryArgs
        const getTripsArgCombinations: GetTripsArgs[] = [
          {},
          { booked: true },
          { timing: "upcoming" },
          { timing: "upcoming", booked: true },
          { timing: "unscheduled" },
          { timing: "unscheduled", booked: true },
          { timing: "exclude_past" },
        ]

        getTripsArgCombinations.forEach((args) => {
          try {
            const patch = dispatch(
              tripsApi.util.updateQueryData("getTrips", args, (draft) => {
                draft.trips = draft.trips.filter((t) => t.ref !== tripRef)
              })
            )
            patches.push(patch)
          } catch {
            // cache entry doesn't exist for these args, ignore
          }
        })

        // update NavTrips - try with empty args (default) and with search
        const getNavTripsArgCombinations: GetNavTripsArgs[] = [
          {}, // default (no search)
        ]

        getNavTripsArgCombinations.forEach((args) => {
          try {
            const patch = dispatch(
              tripsApi.util.updateQueryData("getNavTrips", args, (draft) => {
                draft.trips = draft.trips.filter((t) => t.ref !== tripRef)
              })
            )
            patches.push(patch)
          } catch {
            // cache entry doesn't exist for these args, ignore
          }
        })

        try {
          await queryFulfilled
        } catch {
          // revert all patches on failure
          patches.forEach((patch) => patch.undo())
        }
      },
      invalidatesTags: (_result, _error, tripRef) => [
        { type: "Trip", id: tripRef },
        "NavTrips",
        "Trips",
        "PlaceTripRefs",
      ],
    }),
    getTripDetails: builder.query<GetTripDetailsResponse, GetTripDetailsArgs>({
      query: ({ tripRef, page = 1 }) => ({
        url: `/api/trips/${tripRef}/details`,
        params: { page },
      }),
      providesTags: (_result, _error, { tripRef }) => [
        { type: "TripDetails", id: tripRef },
      ],
      serializeQueryArgs: ({ queryArgs }) => {
        return `details-${queryArgs.tripRef}`
      },
      merge: (current, incoming, { arg }) => {
        // Merge pagination results
        if (arg.page && arg.page > 1 && current) {
          return {
            ...incoming,
            trip_details: [...current.trip_details, ...incoming.trip_details],
          }
        }
        return incoming
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.page !== previousArg?.page
      },
    }),
    createTripDetail: builder.mutation<
      { trip_detail: TripDetail; trip_detail_count: number },
      { tripRef: TripRef; text: string }
    >({
      query: ({ tripRef, text }) => ({
        url: `/api/trips/${tripRef}/details`,
        method: "POST",
        data: { trip_detail: { text } },
      }),
      async onQueryStarted({ tripRef, text }, { dispatch, queryFulfilled }) {
        const patches: Array<{ undo: () => void }> = []
        const tempId = nowMillis()

        // Optimistically add to trip details
        try {
          const patch = dispatch(
            tripsApi.util.updateQueryData(
              "getTripDetails",
              { tripRef },
              (draft) => {
                const newDetail: TripDetail = {
                  id: tempId,
                  text,
                  created_at: now(),
                  updated_at: now(),
                }
                draft.trip_details.unshift(newDetail)
                draft.meta.total_count += 1
              }
            )
          )
          patches.push(patch)
        } catch {
          // cache entry doesn't exist, ignore
        }

        try {
          const result = await queryFulfilled
          // Replace optimistic update with real data
          dispatch(
            tripsApi.util.updateQueryData(
              "getTripDetails",
              { tripRef },
              (draft) => {
                const index = draft.trip_details.findIndex(
                  (d) => d.id === tempId
                )
                if (index !== -1) {
                  draft.trip_details[index] = result.data.trip_detail
                }
                draft.meta.total_count = result.data.trip_detail_count
              }
            )
          )
          // Invalidate counts
          dispatch(
            tripsApi.util.invalidateTags([{ type: "TripCounts", id: tripRef }])
          )
        } catch {
          // Revert optimistic updates on failure
          patches.forEach((patch) => patch.undo())
        }
      },
      invalidatesTags: (_result, _error, { tripRef }) => [
        { type: "TripDetails", id: tripRef },
        { type: "TripCounts", id: tripRef },
      ],
    }),
    updateTripDetail: builder.mutation<
      { trip_detail: TripDetail },
      { tripRef: TripRef; detailId: number; text: string }
    >({
      query: ({ tripRef, detailId, text }) => ({
        url: `/api/trips/${tripRef}/details/${detailId}`,
        method: "PUT",
        data: { trip_detail: { text } },
      }),
      async onQueryStarted(
        { tripRef, detailId, text },
        { dispatch, queryFulfilled }
      ) {
        const patches: Array<{ undo: () => void }> = []

        // Optimistically update trip detail
        try {
          const patch = dispatch(
            tripsApi.util.updateQueryData(
              "getTripDetails",
              { tripRef },
              (draft) => {
                const detail = draft.trip_details.find((d) => d.id === detailId)
                if (detail) {
                  detail.text = text
                  detail.updated_at = now()
                }
              }
            )
          )
          patches.push(patch)
        } catch {
          // cache entry doesn't exist, ignore
        }

        try {
          const result = await queryFulfilled
          // Update with real data
          dispatch(
            tripsApi.util.updateQueryData(
              "getTripDetails",
              { tripRef },
              (draft) => {
                const index = draft.trip_details.findIndex(
                  (d) => d.id === detailId
                )
                if (index !== -1) {
                  draft.trip_details[index] = result.data.trip_detail
                }
              }
            )
          )
        } catch {
          // Revert optimistic updates on failure
          patches.forEach((patch) => patch.undo())
        }
      },
      invalidatesTags: (_result, _error, { tripRef }) => [
        { type: "TripDetails", id: tripRef },
      ],
    }),
    deleteTripDetail: builder.mutation<
      void,
      { tripRef: TripRef; detailId: number }
    >({
      query: ({ tripRef, detailId }) => ({
        url: `/api/trips/${tripRef}/details/${detailId}`,
        method: "DELETE",
      }),
      transformResponse: () => undefined,
      async onQueryStarted(
        { tripRef, detailId },
        { dispatch, queryFulfilled }
      ) {
        const patches: Array<{ undo: () => void }> = []

        // Optimistically remove trip detail
        try {
          const patch = dispatch(
            tripsApi.util.updateQueryData(
              "getTripDetails",
              { tripRef },
              (draft) => {
                draft.trip_details = draft.trip_details.filter(
                  (d) => d.id !== detailId
                )
                draft.meta.total_count = Math.max(0, draft.meta.total_count - 1)
              }
            )
          )
          patches.push(patch)
        } catch {
          // cache entry doesn't exist, ignore
        }

        try {
          await queryFulfilled
          // Invalidate counts
          dispatch(
            tripsApi.util.invalidateTags([{ type: "TripCounts", id: tripRef }])
          )
        } catch {
          // Revert optimistic updates on failure
          patches.forEach((patch) => patch.undo())
        }
      },
      invalidatesTags: (_result, _error, { tripRef }) => [
        { type: "TripDetails", id: tripRef },
        { type: "TripCounts", id: tripRef },
      ],
    }),
    getTripRefsForPlaces: builder.query<
      GetTripRefsForPlacesResponse,
      GetTripRefsForPlacesArgs
    >({
      serializeQueryArgs: ({ queryArgs }) => {
        return JSON.stringify({
          placeRefs: [...queryArgs.placeRefs].sort(),
          timing: queryArgs.timing,
          includeTrips: queryArgs.includeTrips,
        })
      },
      query: ({ placeRefs, timing, includeTrips }) => ({
        url: "/api/trips/fetch_refs/places",
        method: "POST",
        data: {
          place_refs: placeRefs,
          timing,
          include_trips: includeTrips,
        },
      }),
      providesTags: (_result, _error, { placeRefs }) =>
        placeRefs.map((placeRef) => ({
          type: "PlaceTripRefs" as const,
          id: placeRef,
        })),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const result = await queryFulfilled
          const refsByPlace = new Map<PlaceRef, TripRef[]>(
            arg.placeRefs.map((placeRef) => [placeRef, []])
          )

          result.data.trip_places.forEach((tripPlace) => {
            refsByPlace.get(tripPlace.place_ref)?.push(tripPlace.trip_ref)
          })

          if (arg.placeRefs.length <= 1) return

          refsByPlace.forEach((tripRefs, placeRef) => {
            void dispatch(
              tripsApi.util.upsertQueryData(
                "getTripRefsForPlaces",
                { placeRefs: [placeRef], timing: arg.timing },
                {
                  trip_places: tripRefs.map((tripRef) => ({
                    place_ref: placeRef,
                    trip_ref: tripRef,
                  })),
                  trips: [],
                }
              )
            )
          })
        } catch {
          // Query errors are surfaced to the caller.
        }
      },
    }),
    updateTrip: builder.mutation<
      { trip: Trip; itinerary?: Itinerary },
      { tripRef: TripRef; tripUpdate: TripUpdate }
    >({
      query: ({ tripRef, tripUpdate }) => ({
        url: `/api/trips/${tripRef}`,
        method: "PUT",
        data: { trip: tripUpdate },
      }),
      async onQueryStarted(
        { tripRef, tripUpdate },
        { dispatch, queryFulfilled }
      ) {
        const patches: Array<{ undo: () => void }> = []

        // Helper to create TripSummary from Trip (extract AbstractTrip properties)
        const tripToSummary = (trip: Trip): TripSummary => {
          return {
            id: trip.id,
            ref: trip.ref,
            name: trip.name,
            owner_ref: trip.owner_ref,
            user_refs: trip.user_refs,
            destination_name: trip.destination?.name,
            destination_entity_ref: trip.destination?.entity_ref,
            time_zone: trip.time_zone,
            user_created: trip.user_created,
            time_frame: trip.time_frame,
            draft_start_date: trip.draft_start_date,
            draft_duration: trip.draft_duration,
            travelers: trip.travelers,
            rooms: trip.rooms,
            cover_photo: trip.cover_photo,
            stops_area_name: trip.stops_area?.name,
            stops_area_entity_ref: trip.stops_area?.entity_ref,
            roadtrip: trip.roadtrip,
          }
        }

        // Optimistically update trip in NavTrips cache
        const getNavTripsArgCombinations: GetNavTripsArgs[] = [{}]
        getNavTripsArgCombinations.forEach((args) => {
          try {
            const patch = dispatch(
              tripsApi.util.updateQueryData("getNavTrips", args, (draft) => {
                const tripIndex = draft.trips.findIndex(
                  (t) => t.ref === tripRef
                )
                if (tripIndex !== -1) {
                  // Merge update into existing trip summary
                  draft.trips[tripIndex] = {
                    ...draft.trips[tripIndex],
                    ...tripUpdate,
                    ref: tripRef,
                  }
                }
              })
            )
            patches.push(patch)
          } catch {
            // cache entry doesn't exist, ignore
          }
        })

        // Optimistically update trip in all possible getTrips cache combinations
        const getTripsArgCombinations: GetTripsArgs[] = [
          {},
          { booked: true },
          { timing: "upcoming" },
          { timing: "upcoming", booked: true },
          { timing: "unscheduled" },
          { timing: "unscheduled", booked: true },
          { timing: "exclude_past" },
        ]

        getTripsArgCombinations.forEach((args) => {
          try {
            const patch = dispatch(
              tripsApi.util.updateQueryData("getTrips", args, (draft) => {
                const tripIndex = draft.trips.findIndex(
                  (t) => t.ref === tripRef
                )
                if (tripIndex !== -1) {
                  // Merge update into existing trip summary
                  draft.trips[tripIndex] = {
                    ...draft.trips[tripIndex],
                    ...tripUpdate,
                    ref: tripRef,
                  }
                }
              })
            )
            patches.push(patch)
          } catch {
            // cache entry doesn't exist, ignore
          }
        })

        try {
          const result = await queryFulfilled
          // Update with real data from server
          const updatedTrip = result.data.trip
          const tripSummary = tripToSummary(updatedTrip)

          // Update getTrip cache with full trip data for immediate access
          void dispatch(
            tripsApi.util.upsertQueryData("getTrip", tripRef, updatedTrip)
          )

          // Update NavTrips with real data
          getNavTripsArgCombinations.forEach((args) => {
            try {
              dispatch(
                tripsApi.util.updateQueryData("getNavTrips", args, (draft) => {
                  const tripIndex = draft.trips.findIndex(
                    (t) => t.ref === tripRef
                  )
                  if (tripIndex !== -1) {
                    draft.trips[tripIndex] = tripSummary
                  }
                })
              )
            } catch {
              // cache entry doesn't exist, ignore
            }
          })

          // Update getTrips with real data
          getTripsArgCombinations.forEach((args) => {
            try {
              dispatch(
                tripsApi.util.updateQueryData("getTrips", args, (draft) => {
                  const tripIndex = draft.trips.findIndex(
                    (t) => t.ref === tripRef
                  )
                  if (tripIndex !== -1) {
                    draft.trips[tripIndex] = tripSummary
                  }
                })
              )
            } catch {
              // cache entry doesn't exist, ignore
            }
          })
        } catch {
          // Revert optimistic updates on failure
          patches.forEach((patch) => patch.undo())
        }
      },
      invalidatesTags: (_result, _error, { tripRef }) => [
        { type: "Trip", id: tripRef },
        // A date change can flip whether an itinerary review is available
        // (a flexible trip has none), so refresh the review status too.
        { type: "ItineraryReview", id: tripRef },
        "NavTrips",
        "Trips",
      ],
    }),
    deleteTrip: builder.mutation<void, TripRef>({
      query: (tripRef) => ({
        url: `/api/trips/${tripRef}`,
        method: "DELETE",
      }),
      transformResponse: () => undefined,
      async onQueryStarted(tripRef, { dispatch, queryFulfilled }) {
        // Optimistically remove the trip from all possible cached queries
        const patches: Array<{ undo: () => void }> = []

        // All possible getTrips cache key combinations
        const getTripsArgCombinations: GetTripsArgs[] = [
          {},
          { booked: true },
          { timing: "upcoming" },
          { timing: "upcoming", booked: true },
          { timing: "unscheduled" },
          { timing: "unscheduled", booked: true },
          { timing: "exclude_past" },
        ]

        getTripsArgCombinations.forEach((args) => {
          try {
            const patch = dispatch(
              tripsApi.util.updateQueryData("getTrips", args, (draft) => {
                draft.trips = draft.trips.filter((t) => t.ref !== tripRef)
              })
            )
            patches.push(patch)
          } catch {
            // cache entry doesn't exist for these args, ignore
          }
        })

        // Update NavTrips
        const getNavTripsArgCombinations: GetNavTripsArgs[] = [{}]

        getNavTripsArgCombinations.forEach((args) => {
          try {
            const patch = dispatch(
              tripsApi.util.updateQueryData("getNavTrips", args, (draft) => {
                draft.trips = draft.trips.filter((t) => t.ref !== tripRef)
              })
            )
            patches.push(patch)
          } catch {
            // cache entry doesn't exist, ignore
          }
        })

        try {
          await queryFulfilled
        } catch {
          // Revert all patches on failure
          patches.forEach((patch) => patch.undo())
        }
      },
      invalidatesTags: (_result, _error, tripRef) => [
        { type: "Trip", id: tripRef },
        "NavTrips",
        "Trips",
        "PlaceTripRefs",
      ],
    }),
    generateTripNameFromStops: builder.mutation<
      string | undefined,
      { tripRef: TripRef; tripUpdate: TripUpdate }
    >({
      query: ({ tripRef, tripUpdate }) => ({
        url: `/api/trips/${tripRef}/generate-name-from-stops`,
        method: "POST",
        data: { trip: tripUpdate },
      }),
      transformResponse: (response: { name?: string }) => response.name,
    }),
    durationFromMajorStops: builder.mutation<
      { time_frame: TimeFrame },
      { tripRef: TripRef; tripUpdate: TripUpdate }
    >({
      query: ({ tripRef, tripUpdate }) => ({
        url: `/api/trips/${tripRef}/duration_from_major_stops`,
        method: "POST",
        data: { trip: tripUpdate },
      }),
    }),
    generatePossibleTripVibes: builder.mutation<TripVibe[], TripUpdate>({
      query: (trip) => ({
        url: "/api/trips/generate-vibes",
        method: "POST",
        data: { trip },
      }),
      transformResponse: (response: { vibes: TripVibe[] }) => response.vibes,
    }),
    addCommissionablePlace: builder.mutation<
      void,
      { tripRef: TripRef; placeRef: PlaceRef; sourceRef: InspirationRef }
    >({
      query: ({ tripRef, placeRef, sourceRef }) => ({
        url: `/api/trips/${tripRef}/add_commissionable_place`,
        method: "POST",
        data: {
          source_ref: sourceRef,
          place_ref: placeRef,
        },
      }),
      transformResponse: () => undefined,
    }),
    getCollaborators: builder.query<Collaborator[], TripRef>({
      query: (tripRef) => ({
        url: `/api/trips/${tripRef}/collaborators`,
      }),
      transformResponse: (response: { collaborators: Collaborator[] }) =>
        response.collaborators,
      providesTags: (_result, _error, tripRef) => [
        { type: "Collaborators", id: tripRef },
      ],
    }),
    removeCollaborator: builder.mutation<
      void,
      { tripRef: TripRef; userRef: UserRef }
    >({
      query: ({ tripRef, userRef }) => ({
        url: `/api/trips/${tripRef}/collaborators/remove`,
        method: "POST",
        data: { user_ref: userRef },
      }),
      transformResponse: () => undefined,
      invalidatesTags: (_result, _error, { tripRef }) => [
        { type: "Collaborators", id: tripRef },
      ],
    }),
    getTripCoverPhotoOptions: builder.query<PlacePhoto[], TripRef>({
      query: (tripRef) => ({
        url: `/api/trips/${tripRef}/cover-photo-options`,
      }),
      transformResponse: (response: { photos: PlacePhoto[] }) =>
        response.photos,
    }),
    uploadTripCoverPhoto: builder.mutation<
      { url: string; width?: number; height?: number },
      { tripRef: TripRef; file: File }
    >({
      query: ({ tripRef, file }) => {
        const formData = new FormData()
        formData.append("file", file)

        return {
          url: `/api/trips/${tripRef}/upload-cover-photo`,
          method: "POST",
          data: formData,
          headers: {
            "content-type": "multipart/form-data",
          },
        }
      },
      invalidatesTags: (_result, _error, { tripRef }) => [
        { type: "Trip", id: tripRef },
        "NavTrips",
        "Trips",
      ],
    }),
    updateTripCoverPhoto: builder.mutation<
      { trip: Trip },
      { tripRef: TripRef; photo: PlacePhoto | null }
    >({
      query: ({ tripRef, photo }) => ({
        url: `/api/trips/${tripRef}/cover-photo`,
        method: "PUT",
        data: { photo },
      }),
      invalidatesTags: (_result, _error, { tripRef }) => [
        { type: "Trip", id: tripRef },
        "NavTrips",
        "Trips",
      ],
    }),
    extractTripFromText: builder.mutation<TripTextExtraction, { text: string }>(
      {
        query: ({ text }) => ({
          url: `/api/trips/extract-from-text`,
          method: "POST",
          data: { text },
        }),
      }
    ),
    createTripFromText: builder.mutation<
      Trip,
      { tripUpdate: TripUpdate; text: string }
    >({
      query: ({ tripUpdate, text }) => ({
        url: `/api/trips/from-text`,
        method: "POST",
        data: { trip: tripUpdate, text },
      }),
      transformResponse: (response: { trip: Trip }) => response.trip,
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(userSlice.actions.patchUser({ has_any_trip: true }))
        } catch {
          // query failure is surfaced through the mutation result
        }
      },
      invalidatesTags: ["NavTrips", "Trips"],
    }),
    updateTripFromText: builder.mutation<
      { trip: Trip; itinerary?: Itinerary },
      { tripRef: TripRef; tripUpdate: TripUpdate; text: string }
    >({
      query: ({ tripRef, tripUpdate, text }) => ({
        url: `/api/trips/${tripRef}/from-text`,
        method: "PUT",
        data: { trip: tripUpdate, text },
      }),
      invalidatesTags: (_result, _error, { tripRef }) => [
        { type: "Trip", id: tripRef },
        "NavTrips",
        "Trips",
      ],
    }),
  }),
})

/**
 * Populates the RTK Query cache with trip data.
 * Used when trip data comes from sources other than the getTrip query
 * (e.g., from chat initialization or streaming updates).
 *
 * Returns a promise that resolves when the cache is updated.
 * Await this if you need the trip to be available in selectors immediately after.
 */
export const populateTripCache = (dispatch: AppDispatch, trip: Trip) => {
  return dispatch(tripsApi.util.upsertQueryData("getTrip", trip.ref, trip))
}

/**
 * Updates an existing trip in the RTK Query cache with partial data.
 * Used for streaming updates and optimistic updates.
 *
 * If getState is provided, ensures the cache entry exists before updating.
 * If the cache entry doesn't exist and updates includes a full trip (with ref),
 * it will create the entry instead of silently failing.
 */
export const updateTripCache = (
  dispatch: AppDispatch,
  tripRef: TripRef,
  updates: Partial<Trip>,
  getState?: () => RootState
) => {
  // If getState is provided, check if cache entry exists first
  if (getState) {
    const existingData =
      tripsApi.endpoints.getTrip.select(tripRef)(getState()).data
    if (!existingData) {
      // If we have a full trip object (with ref), upsert it
      if ("ref" in updates && updates.ref === tripRef) {
        void dispatch(
          tripsApi.util.upsertQueryData("getTrip", tripRef, updates as Trip)
        )
        return
      }
      // Otherwise, can't update a non-existent entry with partial data
      return
    }
  }

  dispatch(
    tripsApi.util.updateQueryData("getTrip", tripRef, (draft) => {
      Object.assign(draft, updates)
    })
  )
}

/**
 * Updates the collaborators cache for a trip by marking certain collaborators as active.
 * This is used for presence updates from websockets.
 */
export const updateCollaboratorsCache = (
  dispatch: AppDispatch,
  tripRef: TripRef,
  activeUserRefs: UserRef[]
) => {
  dispatch(
    tripsApi.util.updateQueryData("getCollaborators", tripRef, (draft) => {
      draft.forEach((collaborator) => {
        collaborator.active = activeUserRefs.includes(
          collaborator.user.user_ref
        )
      })
    })
  )
}

/**
 * Updates the collaborators cache by adding a new collaborator.
 * Used when receiving collaborator_added events from websockets.
 */
export const addCollaboratorToCache = (
  dispatch: AppDispatch,
  tripRef: TripRef,
  collaborator: Collaborator
) => {
  dispatch(
    tripsApi.util.updateQueryData("getCollaborators", tripRef, (draft) => {
      const existingIndex = draft.findIndex(
        (c) => c.user.user_ref === collaborator.user.user_ref
      )
      if (existingIndex === -1) {
        draft.push(collaborator)
      } else {
        draft[existingIndex] = collaborator
      }
    })
  )
}

/**
 * Updates the collaborators cache by removing a collaborator.
 * Used when receiving collaborator_removed events from websockets.
 */
export const removeCollaboratorFromCache = (
  dispatch: AppDispatch,
  tripRef: TripRef,
  userRef: UserRef
) => {
  dispatch(
    tripsApi.util.updateQueryData("getCollaborators", tripRef, (draft) => {
      const index = draft.findIndex((c) => c.user.user_ref === userRef)
      if (index !== -1) {
        draft.splice(index, 1)
      }
    })
  )
}

export const {
  useGetNavTripsQuery,
  useLazyGetNavTripsQuery,
  useGetTripsCalendarEventsQuery,
  useLazyGetTripsCalendarEventsQuery,
  useGetTripsQuery,
  useLazyGetTripsQuery,
  useGetTripQuery,
  useLazyGetTripQuery,
  useGetTripCountsQuery,
  useLazyGetTripCountsQuery,
  useGetTripAdvicesQuery,
  useGetItineraryReviewQuery,
  useStartItineraryReviewMutation,
  useLazyGetTripAdvicesQuery,
  useGetContextualTripAdvicesQuery,
  useDismissTripAdviceMutation,
  useSnoozeTripAdvicesMutation,
  useUnsnoozeTripAdvicesMutation,
  useRecordTripAdvicePillClickMutation,
  useRecordTripAdviceViewMutation,
  useLazyGetContextualTripAdvicesQuery,
  useLeaveTripMutation,
  useGetTripDetailsQuery,
  useLazyGetTripDetailsQuery,
  useCreateTripDetailMutation,
  useUpdateTripDetailMutation,
  useDeleteTripDetailMutation,
  useGetTripRefsForPlacesQuery,
  useLazyGetTripRefsForPlacesQuery,
  useUpdateTripMutation,
  useDeleteTripMutation,
  useGenerateTripNameFromStopsMutation,
  useDurationFromMajorStopsMutation,
  useGeneratePossibleTripVibesMutation,
  useExtractTripFromTextMutation,
  useAddCommissionablePlaceMutation,
  useGetCollaboratorsQuery,
  useLazyGetCollaboratorsQuery,
  useRemoveCollaboratorMutation,
  useCreateTripFromTextMutation,
  useUpdateTripFromTextMutation,
  useGetTripCoverPhotoOptionsQuery,
  useLazyGetTripCoverPhotoOptionsQuery,
  useUploadTripCoverPhotoMutation,
  useUpdateTripCoverPhotoMutation,
} = tripsApi
